import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase';
import type { CalendarEvent } from '../types';
import './Admin.css';

type PendingEvent = CalendarEvent & {
  category?: { name: string } | null;
};

type AdminUserRow = {
  user_id: string;
  created_at: string;
};

const ADMIN_PASSWORD = 'Pw45Ut09';
const ADMIN_UNLOCK_KEY = 'rec_admin_unlocked_v1';

export default function Admin() {
  const [events, setEvents] = useState<PendingEvent[]>([]);
  const [allEvents, setAllEvents] = useState<PendingEvent[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [lockError, setLockError] = useState<string | null>(null);
  const [unlocked, setUnlocked] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem(ADMIN_UNLOCK_KEY) === '1';
  });

  const stats = useMemo(
    () => ({
      pending: events.length,
      total: allEvents.length,
      published: allEvents.filter((event) => event.status === 'published').length,
      draft: allEvents.filter((event) => event.status === 'draft').length,
    }),
    [events, allEvents]
  );

  const loadPending = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: e } = await supabase
      .from('events')
      .select('*, category:event_categories(name)')
      .eq('status', 'draft')
      .order('created_at', { ascending: false });

    if (e) {
      setError(e.message);
      setLoading(false);
      return;
    }
    setEvents((data as PendingEvent[]) ?? []);

    const { data: allData, error: allError } = await supabase
      .from('events')
      .select('*, category:event_categories(name)')
      .order('created_at', { ascending: false })
      .limit(200);
    if (allError) setError(allError.message);
    else setAllEvents((allData as PendingEvent[]) ?? []);

    const { data: adminData } = await supabase
      .from('admin_users')
      .select('user_id, created_at')
      .order('created_at', { ascending: false });
    setAdminUsers((adminData as AdminUserRow[]) ?? []);

    setLoading(false);
  }, []);

  useEffect(() => {
    if (!unlocked) return;
    loadPending();
  }, [unlocked, loadPending]);

  async function publishEvent(eventId: string) {
    setBusyId(eventId);
    setError(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: e } = await (supabase.from('events') as any).update({ status: 'published' }).eq('id', eventId);
    setBusyId(null);
    if (e) {
      setError(e.message);
      return;
    }
    setEvents((prev) => prev.filter((ev) => ev.id !== eventId));
    setAllEvents((prev) => prev.map((ev) => (ev.id === eventId ? { ...ev, status: 'published' } : ev)));
  }

  async function rejectEvent(eventId: string) {
    setBusyId(eventId);
    setError(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: e } = await (supabase.from('events') as any).delete().eq('id', eventId).eq('status', 'draft');
    setBusyId(null);
    if (e) {
      setError(e.message);
      return;
    }
    setEvents((prev) => prev.filter((ev) => ev.id !== eventId));
    setAllEvents((prev) => prev.filter((ev) => ev.id !== eventId));
  }

  function unlockAdmin(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      setUnlocked(true);
      setLockError(null);
      window.sessionStorage.setItem(ADMIN_UNLOCK_KEY, '1');
      setPasswordInput('');
      return;
    }
    setLockError('Incorrect password');
  }

  function lockAdmin() {
    setUnlocked(false);
    setEvents([]);
    setAllEvents([]);
    setAdminUsers([]);
    window.sessionStorage.removeItem(ADMIN_UNLOCK_KEY);
  }

  if (!unlocked) {
    return (
      <div className="admin-lock-page">
        <section className="admin-lock-card">
          <p className="admin-eyebrow">Admin Access</p>
          <h1>Enter password</h1>
          <p>No email address required. Use the admin password to access approvals and management apps.</p>
          <form className="admin-lock-form" onSubmit={unlockAdmin}>
            <label htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              autoComplete="off"
              required
            />
            {lockError && (
              <div className="admin-error" role="alert">
                {lockError}
              </div>
            )}
            <button type="submit" className="admin-btn admin-btn-publish">
              Open admin dashboard
            </button>
          </form>
        </section>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-page">
        <p className="admin-loading">Loading admin approvals…</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <p className="admin-eyebrow">Admin control center</p>
          <h1>Approvals and account management</h1>
          <p>All event information, approvals, and account controls in one home-screen style dashboard.</p>
        </div>
        <div className="admin-header-actions">
          <Link to="/events" className="admin-link-btn">
            View public calendar
          </Link>
          <button type="button" className="admin-link-btn admin-link-btn-danger" onClick={lockAdmin}>
            Lock admin
          </button>
        </div>
      </div>

      <section className="admin-app-grid" aria-label="Admin apps">
        <article className="admin-app-card">
          <p>Approvals</p>
          <h3>{stats.pending}</h3>
          <small>Pending draft events</small>
        </article>
        <article className="admin-app-card">
          <p>Event library</p>
          <h3>{stats.total}</h3>
          <small>Total events loaded</small>
        </article>
        <article className="admin-app-card">
          <p>Published</p>
          <h3>{stats.published}</h3>
          <small>Live on public calendar</small>
        </article>
        <article className="admin-app-card">
          <p>Drafts</p>
          <h3>{stats.draft}</h3>
          <small>Awaiting decision</small>
        </article>
      </section>

      {error && (
        <div className="admin-error" role="alert">
          {error}
        </div>
      )}

      <section className="admin-section">
        <div className="admin-section-head">
          <h2>Event approvals</h2>
          <p>Review draft submissions and publish approved events to the public calendar.</p>
        </div>
      {events.length === 0 ? (
        <div className="admin-empty">
          <h2>No pending submissions</h2>
          <p>All draft events have been reviewed or are unavailable for this access level.</p>
        </div>
      ) : (
        <ul className="admin-list">
          {events.map((event) => (
            <li key={event.id} className="admin-card">
              <div className="admin-card-main">
                <h2>{event.title}</h2>
                <p className="admin-meta">
                  {event.start_date} to {event.end_date}
                  {event.location ? ` · ${event.location}` : ''}
                  {event.category?.name ? ` · ${event.category.name}` : ''}
                </p>
                {event.description && <p className="admin-description">{event.description}</p>}
                {event.external_url && (
                  <a href={event.external_url} target="_blank" rel="noreferrer" className="admin-ext-link">
                    Open submitted link
                  </a>
                )}
              </div>
              <div className="admin-card-actions">
                <button
                  type="button"
                  className="admin-btn admin-btn-publish"
                  disabled={busyId === event.id}
                  onClick={() => publishEvent(event.id)}
                >
                  {busyId === event.id ? 'Saving…' : 'Approve & publish'}
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-reject"
                  disabled={busyId === event.id}
                  onClick={() => rejectEvent(event.id)}
                >
                  Reject submission
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      </section>

      <section className="admin-section">
        <div className="admin-section-head">
          <h2>Event library</h2>
          <p>Recently loaded events across all statuses.</p>
        </div>
        {allEvents.length === 0 ? (
          <div className="admin-empty">
            <h2>No events returned</h2>
            <p>Try again later or check database access settings.</p>
          </div>
        ) : (
          <ul className="admin-list">
            {allEvents.slice(0, 30).map((event) => (
              <li key={`all-${event.id}`} className="admin-card">
                <div className="admin-card-main">
                  <h2>{event.title}</h2>
                  <p className="admin-meta">
                    {event.start_date} to {event.end_date}
                    {event.location ? ` · ${event.location}` : ''}
                    {event.category?.name ? ` · ${event.category.name}` : ''}
                    {` · ${event.status}`}
                  </p>
                  {event.description && <p className="admin-description">{event.description}</p>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="admin-section">
        <div className="admin-section-head">
          <h2>Account management</h2>
          <p>Admin account records currently visible to this dashboard.</p>
        </div>
        {adminUsers.length === 0 ? (
          <div className="admin-empty">
            <h2>No admin account records visible</h2>
            <p>Add admin users in Supabase or adjust permissions if required.</p>
          </div>
        ) : (
          <ul className="admin-list">
            {adminUsers.map((row) => (
              <li key={row.user_id} className="admin-card">
                <div className="admin-card-main">
                  <h2>{row.user_id}</h2>
                  <p className="admin-meta">
                    Added {new Date(row.created_at).toLocaleString('en-AU')}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
